import { DryingProjectOverview } from "@/data/models/DryingProjectOverview";

/**
 * One `page_N` overview document, read into the projects it holds.
 *
 * Overview collections store 100 projects per document in a `data` map, so a page is the unit
 * both the Firestore listeners and the archive's slice loader work in.
 */
export interface DryingProjectOverviewPage {
  /** The `N` of `page_N`. */
  pageNumber: number;
  projects: DryingProjectOverview[];
}

/**
 * The part of a Firestore document snapshot a page reader needs.
 *
 * Structural on purpose: it keeps this module free of Firestore imports, which is what lets the
 * archive's slice observer be unit-tested without the app's whole module graph behind it.
 */
export interface OverviewPageDocument {
  id: string;
  get(field: string): unknown;
}

/**
 * Reads one `page_N` overview document into the projects it holds.
 */
export function mapPageDocumentToOverviewPage(
  doc: OverviewPageDocument,
  filter?: (dryingProjectOverview: DryingProjectOverview) => boolean
): DryingProjectOverviewPage {
  const tokens = doc.id.split("_");

  const page: DryingProjectOverviewPage = {
    pageNumber: parseInt(tokens[tokens.length - 1]),
    projects: [],
  };

  const data = (doc.get("data") ?? {}) as Record<string, DryingProjectOverview>;

  Object.keys(data).forEach((key) => {
    const overview = data[key];

    if (overview && overview.id && (!filter || filter(overview))) {
      page.projects.push(overview);
    }
  });

  return page;
}

/**
 * Merges overview pages into one project per id, the **highest page number winning**.
 *
 * A project can appear on two pages: its page is derived from its `internalProjectNumber`, so a
 * row written before that number was assigned lands on `page_0` and stays there when the project
 * is later written to its real page. The higher page number is the later, correct one.
 *
 * Pure, and it does not sort its argument, because the archive's slice loader hands it the same
 * accumulated pages again on every snapshot.
 */
export function mergeDryingProjectOverviewPages(
  pages: DryingProjectOverviewPage[]
): DryingProjectOverview[] {
  const result = new Map<string, DryingProjectOverview>();

  [...pages]
    .sort((firstPage, secondPage) => secondPage.pageNumber - firstPage.pageNumber)
    .forEach((page) => {
      page.projects.forEach((project) => {
        if (!result.has(project.id)) {
          result.set(project.id, project);
        }
      });
    });

  return [...result.values()];
}

/**
 * Overview page documents one archive slice listens to.
 *
 * Small enough that the first rows appear early on a slow connection and the progress bar moves
 * often; large enough that the round trip per slice does not dominate the download.
 */
export const ARCHIVE_SLICE_PAGE_COUNT = 10;

/** What a slice's contents say about the rest of the chain. */
export interface ArchiveSliceOutcome {
  /** True when this slice reached the end of the collection. */
  isComplete: boolean;
  /** The document the next slice starts **at**, or `null` when there is no next slice. */
  nextStartAtDocumentId: string | null;
}

/**
 * Decides whether the archive's slice chain continues past this slice, and where from.
 *
 * The next slice starts **at** this one's last document rather than after it, so a page document
 * created while the page is open cannot push a document over a slice boundary into no slice at
 * all. The merge by document id removes the overlap again.
 *
 * @param documentIds - the page documents in this slice, in query order.
 * @param startAtDocumentId - the document this slice itself started at, `null` for the first.
 * @param slicePageCount - the slice's limit; a shorter slice is the end of the collection.
 */
export function resolveArchiveSliceOutcome(
  documentIds: string[],
  startAtDocumentId: string | null,
  slicePageCount: number = ARCHIVE_SLICE_PAGE_COUNT
): ArchiveSliceOutcome {
  const lastDocumentId =
    documentIds.length > 0 ? documentIds[documentIds.length - 1] : null;

  // The overlapping cursor means a full slice ends where the next one starts, so a collection
  // that divides evenly finishes on a slice holding only that one shared document. Guarding on
  // the cursor as well keeps a chain that cannot advance from repeating itself for ever.
  if (
    documentIds.length < slicePageCount ||
    lastDocumentId === null ||
    lastDocumentId === startAtDocumentId
  ) {
    return { isComplete: true, nextStartAtDocumentId: null };
  }

  return { isComplete: false, nextStartAtDocumentId: lastDocumentId };
}
